import { Mail, Briefcase, Pencil, Trash2, AlertCircle, Clock, Star, Calendar, Video, Phone, FileText, CalendarClock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getRelationshipStatusLabel, getRelationshipStatusColor } from "@/lib/client-calculations";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface NextReminder {
  title: string;
  reminder_date: string;
  reminder_time: string | null;
  reminder_type: string;
}

interface ClientCardProps {
  id: string;
  name: string;
  email: string;
  activeProjects: number;
  totalBilled: number;
  totalPending?: number;
  totalOverdue?: number;
  relationshipStatus?: string;
  rating?: number | null;
  tags?: string[];
  nextReminder?: NextReminder | null;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const reminderTypeIcons = {
  meeting: Video,
  call: Phone,
  email: Mail,
  followup: CalendarClock,
  other: FileText,
};

export function ClientCard({
  name,
  email,
  activeProjects,
  totalBilled,
  totalPending = 0,
  totalOverdue = 0,
  relationshipStatus = 'ativo',
  rating,
  tags = [],
  nextReminder,
  onClick,
  onEdit,
  onDelete,
}: ClientCardProps) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const renderRating = () => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3 w-3 ${
              star <= rating
                ? 'fill-warning text-warning'
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatReminderDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Hoje";
    if (isTomorrow(date)) return "Amanhã";
    return format(date, "dd/MM", { locale: ptBR });
  };

  const renderNextReminder = () => {
    if (!nextReminder) return null;
    const Icon = reminderTypeIcons[nextReminder.reminder_type as keyof typeof reminderTypeIcons] || Calendar;
    
    return (
      <div className="flex items-center gap-1.5 text-xs text-primary bg-primary/5 px-2 py-1 rounded-md">
        <Icon className="h-3 w-3" />
        <span className="font-medium">{formatReminderDate(nextReminder.reminder_date)}</span>
        {nextReminder.reminder_time && (
          <span className="text-muted-foreground">{nextReminder.reminder_time.slice(0, 5)}</span>
        )}
        <span className="truncate max-w-[100px]">{nextReminder.title}</span>
      </div>
    );
  };

  return (
    <Card
      className="cursor-pointer shadow-card hover:shadow-card-lg transition-all hover:-translate-y-0.5 animate-fade-in"
      onClick={onClick}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0">
          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 shrink-0">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm sm:text-base">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2 sm:space-y-3 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold truncate text-sm sm:text-base">{name}</h3>
                  <Badge className={`text-xs ${getRelationshipStatusColor(relationshipStatus)}`}>
                    {getRelationshipStatusLabel(relationshipStatus)}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground min-w-0">
                  <Mail className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                  <span className="truncate">{email}</span>
                </div>
                {renderRating()}
              </div>
              {(onEdit || onDelete) && (
                <div className="flex items-center gap-1 shrink-0">
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleEdit}
                      className="h-7 w-7 sm:h-8 sm:w-8"
                    >
                      <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleDelete}
                      className="h-7 w-7 sm:h-8 sm:w-8 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 sm:gap-y-2 text-xs sm:text-sm">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Briefcase className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                <span className="whitespace-nowrap">{activeProjects} projetos</span>
              </div>
              <div className="flex items-center gap-1 text-success font-medium">
                <span className="whitespace-nowrap">R$ {totalBilled.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
            {nextReminder && renderNextReminder()}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.slice(0, 3).map((tag, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
                {tags.length > 3 && (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    +{tags.length - 3}
                  </Badge>
                )}
              </div>
            )}
            {(totalPending > 0 || totalOverdue > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                {totalPending > 0 && (
                  <Badge variant="secondary" className="gap-1 text-xs">
                    <Clock className="h-3 w-3" />
                    R$ {totalPending.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pendente
                  </Badge>
                )}
                {totalOverdue > 0 && (
                  <Badge variant="destructive" className="gap-1 text-xs">
                    <AlertCircle className="h-3 w-3" />
                    R$ {totalOverdue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} atrasado
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}